import React from 'react';
import { Box } from '@mui/material';
import { ComparisonAgreement, DimensionManifest, DimensionMetrics, RunOutcome } from '../../../services/benchmarkService';
import { StatBlock } from '../BenchmarkPrimitives';
import { count, decimal, percent } from '../BenchmarkVocabulary';
import { flagOverlap, flagVerb, isClassDimension, kappaReading, metric } from './investigationVocabulary';

/**
 * A figure computed over too few cases to state as a result. A dimension is
 * judged on its own support, not the run's, so this is the scorer's own flag or
 * its low-confidence label. The run-wide 200-case threshold would mark every
 * investigation run indicative, since they replay at most a few hundred cases.
 */
const weak = (figure: { indicative: boolean; confidence: string | null } | null) =>
    Boolean(figure && (figure.indicative || figure.confidence === 'low'));

/** The outcome the hit rates are read against: the run's first, else confirmed suspicion. */
const outcomeOf = (outcomes?: RunOutcome[]): RunOutcome =>
    outcomes?.[0] ?? { key: 'tp', label: 'Confirmed suspicious', circularFor: 'institution' };

/** How a class dimension's two sides compare: exact, near, and which way they lean. */
const ClassAgreement: React.FC<{ dimension: DimensionManifest; metrics: DimensionMetrics }> = ({ dimension, metrics }) => {
    const exact = metric(metrics, 'exactAgreement');
    const ordinal = dimension.valueKind === 'ordinal';
    const kappa = metric(metrics, ordinal ? 'weightedKappa' : 'kappa');
    const reading = kappaReading(kappa?.value);
    const withinOne = metric(metrics, 'withinOneAgreement');
    const over = metric(metrics, 'overCallRate');
    const under = metric(metrics, 'underCallRate');
    const macroF1 = metric(metrics, 'macroF1');
    return (
        <>
            <StatBlock
                label="Same class"
                value={percent(exact?.value)}
                detail={exact?.sampleSize ? `over ${exact.sampleSize} comparable cases` : undefined}
                definition="The share of comparable cases where the historical record and the agent replay reached exactly the same class."
                indicative={weak(exact)}
            />
            <StatBlock
                label="Agreement beyond chance"
                value={decimal(kappa?.value, 2)}
                technicalName={ordinal ? 'Quadratic-weighted kappa' : "Cohen's kappa"}
                detail={reading ?? undefined}
                definition={ordinal
                    ? 'Agreement beyond chance, from −1 to 1, where a result two classes away counts as worse than one a single class away.'
                    : 'Agreement beyond chance, from −1 to 1. Every disagreement counts the same.'}
                indicative={weak(kappa)}
            />
            {ordinal ? (
                <>
                    <StatBlock
                        label="Within one class"
                        value={percent(withinOne?.value)}
                        definition="The share of comparable cases where the two sides were in the same class or one class apart, for example HIGH against CRITICAL."
                        indicative={weak(withinOne)}
                    />
                    <StatBlock
                        label="Replay rated higher"
                        value={percent(over?.value)}
                        detail={under ? `lower: ${percent(under.value)}` : undefined}
                        definition="The share of comparable cases where the agent replay's class was above the historical one, and below it. A gap between the two is a systematic lean, not noise."
                        indicative={weak(over)}
                    />
                </>
            ) : (
                <StatBlock
                    label="Balanced across classes"
                    value={decimal(macroF1?.value, 2)}
                    technicalName="Macro-averaged F1"
                    definition="How well the agent replay matches the historical record in every class, each class weighted equally, so a rare class counts as much as a common one."
                    indicative={weak(macroF1)}
                />
            )}
        </>
    );
};

/**
 * What one dimension's comparison says, in the figures a reviewer reads first:
 * how often the two sides agree, whether that is more than chance, how often
 * each side says yes, and how often a yes from each met the run's outcome.
 *
 * The last pair is the one that decides adoption: a replay that refers more
 * cases than the historical record is only useful if the extra cases are worth referring.
 * A dimension with classes (an EDD rating, a disposition) leads with its class
 * agreement; its yes/no figures read it through its flagging classes.
 */
const DimensionMetricsStrip: React.FC<{
    /** With its agreement counts where the caller has them, for the flag overlap. */
    dimension: DimensionManifest & { agreementCounts?: Partial<Record<ComparisonAgreement, number>> };
    metrics: DimensionMetrics;
    outcomes?: RunOutcome[];
}> = ({ dimension, metrics, outcomes }) => {
    const verb = flagVerb(dimension);
    const outcome = outcomeOf(outcomes);
    const classes = isClassDimension(dimension);
    const agreement = metric(metrics, 'agreementRate');
    const kappa = metric(metrics, 'kappa');
    const platformRate = metric(metrics, 'platformRate');
    const institutionRate = metric(metrics, 'institutionRate');
    const hitPlatform = metric(metrics, 'hitRatePlatform', outcome.key);
    const hitInstitution = metric(metrics, 'hitRateInstitution', outcome.key);
    const phiPlatform = metric(metrics, 'phiPlatform', outcome.key);
    const phiInstitution = metric(metrics, 'phiInstitution', outcome.key);
    const coverage = metric(metrics, 'coverageRate');
    const rankCorrelation = metric(metrics, 'scoreRankCorrelation');
    const aucPlatform = metric(metrics, 'aucPlatform', outcome.key);
    const aucInstitution = metric(metrics, 'aucInstitution', outcome.key);
    const reading = kappaReading(kappa?.value);
    const outcomeWords = outcome.label.toLowerCase();
    const overlap = dimension.agreementCounts ? flagOverlap(dimension.agreementCounts) : null;

    return (
        <Box className="grid gap-6" sx={{ gridTemplateColumns: 'repeat(auto-fit, minmax(10.5rem, 1fr))' }}>
            {classes ? <ClassAgreement dimension={dimension} metrics={metrics} /> : (
                <>
                    <StatBlock
                        label="Agreement"
                        value={percent(agreement?.value)}
                        detail={agreement?.sampleSize ? `over ${agreement.sampleSize} comparable cases` : undefined}
                        definition="The share of comparable cases where the historical record and the agent replay agreed."
                        indicative={weak(agreement)}
                    />
                    <StatBlock
                        label="Agreement beyond chance"
                        value={decimal(kappa?.value, 2)}
                        technicalName="Cohen's kappa"
                        detail={reading ?? undefined}
                        definition="Agreement after removing what two sides would reach by chance, from −1 to 1. Raw agreement flatters a comparison where both sides mostly say no; this does not."
                        indicative={weak(kappa)}
                    />
                </>
            )}
            {overlap && (
                <StatBlock
                    label="Flag overlap"
                    value={percent(overlap.value, 0)}
                    detail={`both ${verb} ${count(overlap.both)} of the ${count(overlap.either)} either ${verb}`}
                    definition={`Of the cases either side ${verb}, the share both ${verb}. Cases both cleared are left out, so a rare item cannot look well matched just because both sides mostly said no.`}
                    indicative={false}
                />
            )}
            <StatBlock
                label={`Replay ${verb}`}
                value={percent(platformRate?.value)}
                detail={institutionRate ? `historical: ${percent(institutionRate.value)}` : undefined}
                definition={`The share of comparable cases each side ${verb}.`}
                indicative={weak(platformRate)}
            />
            <StatBlock
                label={`${outcome.label} when the replay ${verb}`}
                value={percent(hitPlatform?.value)}
                detail={hitInstitution ? `when the historical record ${verb}: ${percent(hitInstitution.value)}` : undefined}
                definition={`Of the cases each side ${verb}, the share that turned out ${outcomeWords}. Computed only where that side said yes.`}
                accent="#10B981"
                indicative={weak(hitPlatform)}
            />
            {(phiPlatform || phiInstitution) && (
                <StatBlock
                    label={`Goes with ${outcomeWords}`}
                    value={decimal(phiPlatform?.value, 2)}
                    technicalName="Phi correlation"
                    detail={phiInstitution ? `historical: ${decimal(phiInstitution.value, 2)}${outcome.circularFor === 'institution' ? ' (circular)' : ''}` : undefined}
                    definition={`How strongly each side's yes goes with a case turning out ${outcomeWords}, from −1 to 1.`
                        + (outcome.circularFor === 'institution'
                            ? ' The historical figure is inflated by construction: the analysts who recorded it also decided the outcome.'
                            : '')}
                    indicative={weak(phiPlatform)}
                />
            )}
            <StatBlock
                label="Coverage"
                value={percent(coverage?.value)}
                definition="The share of replayed cases where both sides recorded something, so a comparison could be made. The rest are counted here, never as agreement or disagreement."
                detail={coverage && (coverage.value ?? 0) < 1 ? 'see why the rest are not comparable' : undefined}
                indicative={false}
            />
            {rankCorrelation && (
                <StatBlock
                    label="Scores ranked alike"
                    value={decimal(rankCorrelation.value, 2)}
                    technicalName="Spearman rank correlation"
                    definition="Whether the two sides' scores put the cases in the same order, from −1 to 1, over cases where both recorded a score."
                    indicative={weak(rankCorrelation)}
                />
            )}
            {(aucPlatform || aucInstitution) && (
                <StatBlock
                    label={`Separates ${outcomeWords} cases`}
                    value={decimal(aucPlatform?.value, 2)}
                    technicalName={`ROC AUC against ${outcomeWords}`}
                    detail={aucInstitution ? `historical score: ${decimal(aucInstitution.value, 2)}` : undefined}
                    definition={`How well each side's score ranks ${outcomeWords} cases above the rest, from 0.5 (chance) to 1.0.`}
                    indicative={weak(aucPlatform)}
                />
            )}
        </Box>
    );
};

export default DimensionMetricsStrip;
